import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Table, Card, Space, Tag, Input, Select, Button, DatePicker, Row, Col,
  Statistic, message, Tooltip, Badge, Modal, Dropdown
} from 'antd';
import {
  CalendarOutlined, ClockCircleOutlined, SearchOutlined, DownloadOutlined,
  ExclamationCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
  UserOutlined, ShopOutlined, MailOutlined, PhoneOutlined, EyeOutlined,
  MessageOutlined, MoreOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import { exportToExcel } from '../../utils/excelExport';
import BackButton from '../../components/BackButton';

const { RangePicker } = DatePicker;

const AdminAppointmentList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [summary, setSummary] = useState({});
  const [partners, setPartners] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  const [filters, setFilters] = useState({
    status: null,
    partner: null,
    dateRange: null,
    pendingHours: null,
  });

  useEffect(() => {
    loadAppointments();
    loadPartners();
  }, []);

  // Reload when filters change via stat cards
  const [autoReload, setAutoReload] = useState(false);
  useEffect(() => {
    if (autoReload) {
      loadAppointments();
      setAutoReload(false);
    }
  }, [filters, autoReload]);

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.partner) params.partner = filters.partner;
      if (filters.pendingHours) params.pending_hours = filters.pendingHours;
      if (filters.dateRange && filters.dateRange[0]) {
        params.date_from = filters.dateRange[0].format('YYYY-MM-DD');
        params.date_to = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await api.get('/services/admin/appointments/', { params });
      setAppointments(response.data.appointments || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      message.error(t('admin.appointments.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadPartners = async () => {
    try {
      const response = await api.get('/partners/admin/');
      setPartners(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to load partners');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    loadAppointments();
  };

  const clearFilters = () => {
    setFilters({
      status: null,
      partner: null,
      dateRange: null,
      pendingHours: null,
    });
  };

  const showAppointmentDetail = async (appointment) => {
    setSelectedAppointment(appointment);
    setDetailModalVisible(true);
  };

  const handleExport = () => {
    const exportColumns = [
      { title: 'ID', dataIndex: 'id' },
      { title: t('admin.appointments.customer'), dataIndex: 'customer_name' },
      { title: t('admin.appointments.customerEmail'), dataIndex: 'customer_email' },
      { title: t('admin.appointments.partner'), dataIndex: 'partner_name' },
      { title: t('admin.appointments.service'), dataIndex: 'service_name' },
      { title: t('admin.appointments.date'), dataIndex: 'appointment_date' },
      { title: t('admin.appointments.time'), dataIndex: 'start_time' },
      { title: t('admin.appointments.status'), dataIndex: 'status' },
      { title: t('admin.appointments.submittedAt'), dataIndex: 'created_at' },
      { title: t('admin.appointments.confirmedAt'), dataIndex: 'confirmed_at' },
      { title: t('admin.appointments.responseTime'), dataIndex: 'response_time' },
      { title: t('admin.appointments.waitingTime'), dataIndex: 'waiting_time' },
      { title: t('admin.appointments.overdue'), dataIndex: 'is_overdue', exportRender: (val) => val ? 'Yes' : 'No' },
    ];
    exportToExcel(appointments, exportColumns, 'appointments', t('admin.appointments.title'));
    message.success(t('admin.appointments.exportSuccess'));
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'orange',
      CONFIRMED: 'blue',
      COMPLETED: 'green',
      CANCELLED: 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      PENDING: <ClockCircleOutlined />,
      CONFIRMED: <CheckCircleOutlined />,
      COMPLETED: <CheckCircleOutlined />,
      CANCELLED: <CloseCircleOutlined />
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      fixed: 'left',
    },
    {
      title: t('admin.appointments.customer'),
      key: 'customer',
      width: 180,
      ellipsis: true,
      render: (_, record) => (
        <Tooltip title={`${record.customer_name} - ${record.customer_email}`}>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <UserOutlined /> {record.customer_name}
            </div>
            <div style={{ fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {record.customer_email}
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: t('admin.appointments.partner'),
      key: 'partner',
      width: 180,
      ellipsis: true,
      render: (_, record) => (
        <Tooltip title={`${record.partner_name} - ${record.partner_email}`}>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <ShopOutlined /> {record.partner_name}
            </div>
            <div style={{ fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {record.partner_email}
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: t('admin.appointments.service'),
      dataIndex: 'service_name',
      key: 'service_name',
      width: 150,
      ellipsis: true,
      render: (name) => (
        <Tooltip title={name}>
          <span>{name}</span>
        </Tooltip>
      ),
    },
    {
      title: t('admin.appointments.dateTime'),
      key: 'datetime',
      width: 130,
      render: (_, record) => (
        <div>
          <div><CalendarOutlined /> {record.appointment_date}</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            <ClockCircleOutlined /> {record.start_time} - {record.end_time}
          </div>
        </div>
      ),
    },
    {
      title: t('admin.appointments.status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status}
        </Tag>
      ),
    },
    {
      title: t('admin.appointments.responseTime'),
      key: 'response',
      width: 140,
      ellipsis: true,
      render: (_, record) => {
        if (record.status === 'PENDING') {
          return (
            <Tooltip title={t('admin.appointments.waitingSince', { time: record.waiting_time })}>
              <Badge
                status={record.is_overdue ? 'error' : 'warning'}
                text={
                  <span style={{ color: record.is_overdue ? '#ff4d4f' : '#faad14', fontSize: 12 }}>
                    {record.is_overdue && <ExclamationCircleOutlined style={{ marginRight: 4 }} />}
                    {record.waiting_time}
                  </span>
                }
              />
            </Tooltip>
          );
        }
        if (record.response_time) {
          const isGood = record.response_time_hours < 24;
          return (
            <span style={{ color: isGood ? '#52c41a' : '#faad14' }}>
              {record.response_time}
            </span>
          );
        }
        return '-';
      },
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => {
        const contactMenuItems = [
          {
            key: 'customer-header',
            type: 'group',
            label: t('admin.appointments.contactCustomer'),
            children: [
              {
                key: 'customer-email',
                icon: <MailOutlined />,
                label: t('admin.appointments.emailCustomer'),
                onClick: () => window.open(`mailto:${record.customer_email}?subject=Appointment #${record.id} - ${record.service_name}&body=Dear ${record.customer_name},%0A%0ARegarding your appointment (ID: ${record.id}) for ${record.service_name} on ${record.appointment_date}.%0A%0ABest regards,%0AAnimap Team`),
              },
              record.customer_phone && {
                key: 'customer-phone',
                icon: <PhoneOutlined />,
                label: `${t('admin.appointments.callCustomer')}: ${record.customer_phone}`,
                onClick: () => window.open(`tel:${record.customer_phone}`),
              },
            ].filter(Boolean),
          },
          {
            key: 'partner-header',
            type: 'group',
            label: t('admin.appointments.contactPartner'),
            children: [
              {
                key: 'partner-email',
                icon: <MailOutlined />,
                label: t('admin.appointments.emailPartner'),
                onClick: () => window.open(`mailto:${record.partner_email}?subject=Appointment #${record.id} - ${record.service_name}&body=Dear Partner,%0A%0ARegarding appointment (ID: ${record.id}) for ${record.service_name} on ${record.appointment_date}.%0A%0ACustomer: ${record.customer_name} (${record.customer_email})%0A%0ABest regards,%0AAnimap Team`),
              },
              record.partner_phone && {
                key: 'partner-phone',
                icon: <PhoneOutlined />,
                label: `${t('admin.appointments.callPartner')}: ${record.partner_phone}`,
                onClick: () => window.open(`tel:${record.partner_phone}`),
              },
            ].filter(Boolean),
          },
        ];

        return (
          <Space size="small">
            <Tooltip title={t('common.view')}>
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => showAppointmentDetail(record)}
              />
            </Tooltip>
            <Dropdown
              menu={{ items: contactMenuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Tooltip title={t('admin.appointments.contactOptions')}>
                <Button
                  type="link"
                  icon={<MessageOutlined />}
                  style={{ color: record.is_overdue ? '#ff4d4f' : '#1890ff' }}
                />
              </Tooltip>
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/admin/dashboard" label={t('admin.backToDashboard')} />

      {/* Summary Stats - Clickable */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title={t('admin.appointments.clickToShowAll')}>
            <Card
              hoverable
              onClick={() => {
                setFilters({ status: null, partner: null, dateRange: null, pendingHours: null });
                setAutoReload(true);
              }}
              style={{ cursor: 'pointer' }}
            >
              <Statistic
                title={t('admin.appointments.totalAppointments')}
                value={summary.total || 0}
                prefix={<CalendarOutlined />}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title={t('admin.appointments.clickToShowPending')}>
            <Card
              hoverable
              onClick={() => {
                setFilters({ status: 'PENDING', partner: null, dateRange: null, pendingHours: null });
                setAutoReload(true);
              }}
              style={{ cursor: 'pointer', border: filters.status === 'PENDING' && !filters.pendingHours ? '2px solid #faad14' : undefined }}
            >
              <Statistic
                title={t('admin.appointments.pendingAppointments')}
                value={summary.pending || 0}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title={t('admin.appointments.clickToShowOverdue')}>
            <Card
              hoverable
              onClick={() => {
                setFilters({ status: 'PENDING', partner: null, dateRange: null, pendingHours: 24 });
                setAutoReload(true);
              }}
              style={{
                cursor: 'pointer',
                backgroundColor: summary.overdue > 0 ? '#fff2f0' : undefined,
                border: filters.pendingHours === 24 ? '2px solid #ff4d4f' : undefined
              }}
            >
              <Statistic
                title={t('admin.appointments.overdueAppointments')}
                value={summary.overdue || 0}
                prefix={<ExclamationCircleOutlined />}
                valueStyle={{ color: summary.overdue > 0 ? '#ff4d4f' : '#52c41a' }}
              />
            </Card>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Tooltip title={t('admin.appointments.clickToShowConfirmed')}>
            <Card
              hoverable
              onClick={() => {
                setFilters({ status: 'CONFIRMED', partner: null, dateRange: null, pendingHours: null });
                setAutoReload(true);
              }}
              style={{ cursor: 'pointer', border: filters.status === 'CONFIRMED' ? '2px solid #1890ff' : undefined }}
            >
              <Statistic
                title={t('admin.appointments.avgResponseTime')}
                value={summary.avg_response_hours ? `${summary.avg_response_hours.toFixed(1)}h` : '-'}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: summary.avg_response_hours && summary.avg_response_hours < 24 ? '#52c41a' : '#faad14' }}
              />
            </Card>
          </Tooltip>
        </Col>
      </Row>

      <Card
        title={
          <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
            <CalendarOutlined style={{ marginRight: 8 }} />
            {t('admin.appointments.title')}
          </span>
        }
      >
        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder={t('admin.appointments.filterByStatus')}
              style={{ width: '100%' }}
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Select.Option value="PENDING">{t('admin.appointments.statusPending')}</Select.Option>
              <Select.Option value="CONFIRMED">{t('admin.appointments.statusConfirmed')}</Select.Option>
              <Select.Option value="COMPLETED">{t('admin.appointments.statusCompleted')}</Select.Option>
              <Select.Option value="CANCELLED">{t('admin.appointments.statusCancelled')}</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder={t('admin.appointments.filterByPartner')}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
              value={filters.partner}
              onChange={(value) => handleFilterChange('partner', value)}
            >
              {partners.map(p => (
                <Select.Option key={p.id} value={p.id}>
                  {p.business_name || p.store_name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(dates) => handleFilterChange('dateRange', dates)}
              placeholder={[t('admin.appointments.dateFrom'), t('admin.appointments.dateTo')]}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder={t('admin.appointments.filterOverdue')}
              style={{ width: '100%' }}
              allowClear
              value={filters.pendingHours}
              onChange={(value) => handleFilterChange('pendingHours', value)}
            >
              <Select.Option value={12}>{t('admin.appointments.pendingOver12h')}</Select.Option>
              <Select.Option value={24}>{t('admin.appointments.pendingOver24h')}</Select.Option>
              <Select.Option value={48}>{t('admin.appointments.pendingOver48h')}</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Button type="primary" onClick={applyFilters}>
                {t('admin.applyFilters')}
              </Button>
              <Button onClick={clearFilters}>
                {t('admin.clearFilters')}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                disabled={!appointments.length}
              >
                {t('admin.exportExcel')}
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={appointments}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1050 }}
          pagination={{ pageSize: 20 }}
          rowClassName={(record) => record.is_overdue ? 'row-overdue' : ''}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={`${t('admin.appointments.appointmentDetails')} #${selectedAppointment?.id}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button
            key="email-customer"
            icon={<MailOutlined />}
            onClick={() => window.open(`mailto:${selectedAppointment?.customer_email}?subject=Appointment #${selectedAppointment?.id}&body=Dear ${selectedAppointment?.customer_name},%0A%0ARegarding your appointment for ${selectedAppointment?.service_name} on ${selectedAppointment?.appointment_date}.%0A%0ABest regards,%0AAnimap Team`)}
          >
            {t('admin.appointments.emailCustomer')}
          </Button>,
          <Button
            key="email-partner"
            icon={<MailOutlined />}
            onClick={() => window.open(`mailto:${selectedAppointment?.partner_email}?subject=Appointment #${selectedAppointment?.id}&body=Dear Partner,%0A%0ARegarding appointment #${selectedAppointment?.id} for ${selectedAppointment?.service_name}.%0A%0ABest regards,%0AAnimap Team`)}
          >
            {t('admin.appointments.emailPartner')}
          </Button>,
          <Button key="close" type="primary" onClick={() => setDetailModalVisible(false)}>
            {t('common.close')}
          </Button>
        ]}
        width={600}
      >
        {selectedAppointment && (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <h4>{t('admin.appointments.customerInfo')}</h4>
                <p><UserOutlined /> {selectedAppointment.customer_name}</p>
                <p><MailOutlined /> {selectedAppointment.customer_email}</p>
              </Col>
              <Col span={12}>
                <h4>{t('admin.appointments.partnerInfo')}</h4>
                <p><ShopOutlined /> {selectedAppointment.partner_name}</p>
                <p><MailOutlined /> {selectedAppointment.partner_email}</p>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <h4>{t('admin.appointments.serviceInfo')}</h4>
                <p>{selectedAppointment.service_name}</p>
                {selectedAppointment.pet_name && (
                  <p>{t('admin.appointments.pet')}: {selectedAppointment.pet_name}</p>
                )}
              </Col>
              <Col span={12}>
                <h4>{t('admin.appointments.timing')}</h4>
                <p><CalendarOutlined /> {selectedAppointment.appointment_date}</p>
                <p><ClockCircleOutlined /> {selectedAppointment.start_time} - {selectedAppointment.end_time}</p>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={24}>
                <h4>{t('admin.appointments.statusHistory')}</h4>
                <p>{t('admin.appointments.submitted')}: {dayjs(selectedAppointment.created_at).format('DD/MM/YYYY HH:mm')}</p>
                {selectedAppointment.confirmed_at && (
                  <p>{t('admin.appointments.confirmed')}: {dayjs(selectedAppointment.confirmed_at).format('DD/MM/YYYY HH:mm')}</p>
                )}
                {selectedAppointment.completed_at && (
                  <p>{t('admin.appointments.completed')}: {dayjs(selectedAppointment.completed_at).format('DD/MM/YYYY HH:mm')}</p>
                )}
                {selectedAppointment.cancelled_at && (
                  <p>{t('admin.appointments.cancelled')}: {dayjs(selectedAppointment.cancelled_at).format('DD/MM/YYYY HH:mm')}</p>
                )}
              </Col>
            </Row>
            {(selectedAppointment.customer_notes || selectedAppointment.partner_notes) && (
              <Row gutter={16} style={{ marginTop: 16 }}>
                {selectedAppointment.customer_notes && (
                  <Col span={12}>
                    <h4>{t('admin.appointments.customerNotes')}</h4>
                    <p>{selectedAppointment.customer_notes}</p>
                  </Col>
                )}
                {selectedAppointment.partner_notes && (
                  <Col span={12}>
                    <h4>{t('admin.appointments.partnerNotes')}</h4>
                    <p>{selectedAppointment.partner_notes}</p>
                  </Col>
                )}
              </Row>
            )}
          </div>
        )}
      </Modal>

      <style>{`
        .row-overdue {
          background-color: #fff2f0 !important;
        }
        .row-overdue:hover td {
          background-color: #ffccc7 !important;
        }
      `}</style>
    </div>
  );
};

export default AdminAppointmentList;
